import { useTranslation } from "react-i18next"
import { useMemo } from "react"
import { cloneDeep } from "lodash"

import { VALUE_POSITION_TYPE } from "@/app/report/common/constants"
import PrintableSection from "@/app/report/components/section/pdf/PrintableSection"
import { ChartData, SectionGraph } from "@/app/report/common/constructor"
import MultipleChart from "@/app/report/components/chart/pdf/MultipleChart"
import PrintableVerticalPlayerChart from "@/app/report/components/chart/pdf/VerticalPlayerChart"
import { resolveGraphOrientation } from "@/app/report/common/sections/graph"
import {
  chunkVerticalRows,
  collectVerticalHighest,
  VERTICAL_PLAYERS_PER_PAGE_DEFAULT,
} from "@/app/report/common/chart/verticalPlayerChart"
import {
  MAXIMUM_CHART_LEN_BY_BAR_CHART,
  countBarChartAxes,
  getReferenceItemCount,
} from "@/app/report/common/export/constants"

export default function PrintableChartByPlayerSection({
  data,
  isFirst = false,
  scaleRatio = 1,
  currentDecimalPoint = 1,
  currentChartAnnotation = VALUE_POSITION_TYPE.HIDE_VALUES,
}: {
  data: SectionGraph
  isFirst: boolean
  scaleRatio: number
  currentDecimalPoint?: number
  currentChartAnnotation?: VALUE_POSITION_TYPE
}) {
  const { t } = useTranslation()

  // highestValues: 분할된 각 조각(flat index 순)이 속한 원본 차트의 시리즈별 최대값.
  // 분할 차트들이 동일한 축 스케일을 쓰도록 MultipleChart 에 전달한다.
  // referenceItemCounts: 조각(flat index 순)이 속한 원본 차트의 기준 항목 개수.
  // 조각마다 막대 폭이 달라지지 않게 MultipleChart 에 전달한다.
  const { splittedChartData, highestValues, referenceItemCounts, stackedSums } = useMemo(() => {
    const highestValues: number[][] = []
    const referenceItemCounts: number[] = []
    // 세로 차트 묶음의 누적 막대 합 최대값(조각 flat index 순). 가로 차트 조각은 undefined
    const stackedSums: Array<number | undefined> = []

    const splittedChartData = data.charts
      ?.map((chart) => {
        const copied = cloneDeep(chart.data)
        const { settings } = chart
        const barChartCount = countBarChartAxes(settings?.yAxis)

        // 세로(선수 세로 나열) — 장당 선수 수(기본 25)로 잘라 조각마다 한 장. 모든 조각이 같은 스케일을 쓴다 (#185)
        if (resolveGraphOrientation(settings) === "vertical") {
          const { highest, highestStackedSum } = collectVerticalHighest(chart.data, chart.axisY)

          return chunkVerticalRows(chart.data, VERTICAL_PLAYERS_PER_PAGE_DEFAULT).map((rows) => {
            highestValues.push(highest)
            referenceItemCounts.push(0)
            stackedSums.push(highestStackedSum)

            return [{ ...chart, data: rows } as ChartData]
          })
        }

        const mergedChartData = []
        const splitByPage = []

        const highest = chart.data.reduce((acc, cur) => {
          if (acc.length === 0) {
            return cur.data as number[]
          }

          return acc.map((data, index) => Math.max(data, cur.data[index] as number))
        }, [] as number[])

        while (copied.length > 0) {
          mergedChartData.push(copied.splice(0, MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount]))
        }

        const referenceItemCount = getReferenceItemCount(barChartCount, chart.data.length)

        const transformed: ChartData[] = mergedChartData.map((data) => {
          highestValues.push(highest)
          referenceItemCounts.push(referenceItemCount)
          stackedSums.push(undefined)

          return {
            ...chart,
            data,
          }
        })

        while (transformed.length > 0) {
          splitByPage.push(transformed.splice(0, 4 - (isFirst && splitByPage.length === 0 ? 1 : 0)))
        }

        return splitByPage
      })
      .flat()

    return { splittedChartData, highestValues, referenceItemCounts, stackedSums }
  }, [data])

  const getIsSplitted = (chart: ChartData, chartList: ChartData[]): boolean => {
    const { settings } = chart

    if (!settings || chartList?.length <= 1) {
      return false
    }

    const barChartCount = countBarChartAxes(settings?.yAxis)

    return chart.data.length !== MAXIMUM_CHART_LEN_BY_BAR_CHART[barChartCount]
  }

  return (
    <div className="renewal-pdf-container">
      {splittedChartData?.flat().map((chart, index) => {
        return (
          <PrintableSection
            sectionTitle={t("chartByPlayer")}
            scaleRatio={scaleRatio}
            currentItemCount={index + 1}
            totalItemCount={splittedChartData.flat().length}
            key={`section_chart_by_player_${index}`}>
            {resolveGraphOrientation(chart.settings) === "vertical" ? (
              <PrintableVerticalPlayerChart
                chartData={chart}
                scaleRatio={scaleRatio}
                decimalPoint={currentDecimalPoint}
                chartAnnotation={currentChartAnnotation}
                highest={highestValues[index]}
                highestStackedSum={stackedSums[index]}
              />
            ) : (
              <MultipleChart
                loading={false}
                chartData={chart}
                splitted={getIsSplitted(chart, splittedChartData.flat())}
                maxItemCount={referenceItemCounts[index]}
                scaleRatio={scaleRatio}
                decimalPoint={currentDecimalPoint}
                chartAnnotation={currentChartAnnotation}
                highestValues={highestValues[index]}
              />
            )}
          </PrintableSection>
        )
      })}
    </div>
  )
}
